import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { updateDwell, updateScrollDepth } from "@/lib/fanProfile";

import CursorParticles from "@/components/CursorParticles";
import WatchPartyPage from "@/components/WatchPartyPage";
import WatchPartyAdmin from "@/components/WatchPartyAdmin";

import ArrivalCurtain from "@/components/ArrivalCurtain";
import FixedHeader from "@/components/FixedHeader";
import HeroSection from "@/components/HeroSection";
import NowShowing from "@/components/NowShowing";
import CinematicBand from "@/components/CinematicBand";
import FilmWorlds from "@/components/FilmWorlds";
import EchoesSection from "@/components/EchoesSection";
import InsideSection from "@/components/InsideSection";
import UnseenSection from "@/components/UnseenSection";
import TonightSection from "@/components/TonightSection";
import GiftsSection from "@/components/GiftsSection";
import StayCloseSection from "@/components/StayCloseSection";
import BeyondSection from "@/components/BeyondSection";
import SpotifyPlayer from "@/components/SpotifyPlayer";
import TrailerModal from "@/components/TrailerModal";
import Footer from "@/components/Footer";

const Home = () => {
  const [curtainDone, setCurtainDone] = useState(false);
  const [trailerFilm, setTrailerFilm] = useState(null);
  const onCurtainComplete = useCallback(() => setCurtainDone(true), []);

  // Deep-link hash scroll — robust: fires on mount (unconditionally) + after curtain + on hashchange
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (!hash || hash === '#') return;
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      setTimeout(() => {
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Force again in case animation/curtain overrode
          setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
        });
      }, 2000);
    };

    // Fire on mount unconditionally
    scrollToHash();

    // Fire again when curtain finishes (belt + suspenders)
    if (curtainDone) {
      setTimeout(scrollToHash, 500);
    }

    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, [curtainDone]);

  // Dwell time tracker
  useEffect(() => {
    let seconds = 0;
    const interval = setInterval(() => { seconds++; updateDwell(seconds); }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Scroll depth tracker
  useEffect(() => {
    const onScroll = () => {
      const pct = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      updateScrollDepth(pct);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={{ background: 'var(--deep-black)' }} className="min-h-screen">
      {!curtainDone && <ArrivalCurtain onComplete={onCurtainComplete} />}
      <CursorParticles />
      <FixedHeader visible={curtainDone} />
      <HeroSection visible={curtainDone} />
      <NowShowing onTrailer={setTrailerFilm} />
      <CinematicBand />
      <FilmWorlds onTrailer={setTrailerFilm} />
      <EchoesSection />
      <InsideSection />
      <UnseenSection />
      <TonightSection />
      <GiftsSection />
      <StayCloseSection />
      <BeyondSection />
      <SpotifyPlayer />
      {trailerFilm && <TrailerModal film={trailerFilm} onClose={() => setTrailerFilm(null)} />}
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: { background: 'var(--charcoal)', border: '1px solid rgba(201,168,76,0.1)', color: 'var(--warm-cream)', fontFamily: 'Inter, sans-serif' },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/watch-party/:slug" element={<WatchPartyPage />} />
          <Route path="/watch-party/:slug/admin" element={<WatchPartyAdmin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
